import React from 'react'
import { useSearchParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Spinner, Tabs } from '@factorialco/f0-react/dist/experimental'
import {
  ProcurementTypeForDetailFragment,
  WorkflowContainerFragment,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { Form } from 'modules/forms'
import { QuestionnaireType } from 'modules/forms/components/newFormEditor/types'

type TabContentProps = {
  id: string | undefined
  isCompleteFormEnabled: boolean
  isVendorOnboardingEnabled: boolean
  requestType: ProcurementTypeForDetailFragment | undefined
  workflow: WorkflowContainerFragment | undefined
  formModel: Form | undefined
  formState: QuestionnaireType
  setFormState: (state: QuestionnaireType) => void
  isFormLoading: boolean
}

const FormTab = React.lazy(() => import('modules/procurement/components/procurementTypes/Form'))
const WorkflowsTab = React.lazy(
  () => import('modules/procurement/components/procurementTypes/Workflows')
)

const TabContent = (props: TabContentProps): React.JSX.Element => {
  const { id, isCompleteFormEnabled, isVendorOnboardingEnabled, requestType, workflow } = props
  const { navigation } = useNavigation()
  const [searchParams] = useSearchParams()

  // The active tab is from the params or 'form' by default
  const activeTab = React.useMemo(() => searchParams.get('tab') || 'form', [searchParams])

  const formContent = (() => {
    if (props.isFormLoading) {
      return (
        <Box
          flexGrow
          height="full"
          padding="s16"
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
        >
          <Spinner />
        </Box>
      )
    }

    return <FormTab state={props.formState} setState={props.setFormState} />
  })()

  const Content = React.useMemo<React.ReactNode>(() => {
    if (!requestType || !id) {
      return null
    }

    switch (activeTab) {
      case 'workflows':
        return (
          <WorkflowsTab
            isCompleteFormEnabled={isCompleteFormEnabled}
            isVendorOnboardingEnabled={isVendorOnboardingEnabled}
            requestType={requestType}
            workflow={workflow ?? null}
            form={props.formModel}
            isFormLoading={props.isFormLoading}
          />
        )
      case 'form':
      default:
        return formContent
    }
  }, [
    requestType,
    id,
    activeTab,
    isCompleteFormEnabled,
    isVendorOnboardingEnabled,
    workflow,
    props.formModel,
    props.isFormLoading,
    formContent,
  ])

  const path = React.useMemo(() => {
    return navigation.settings.procurement.requestType.id.fullPath({
      id: id || '',
    })
  }, [navigation, id])

  return (
    <>
      <Tabs
        tabs={[
          {
            href: `${path}?tab=form`,
            label: i18n.t('procurement.purchase_requests.settings.types.detail.tabs.form.title'),
            index: true,
          },
          {
            href: `${path}?tab=workflows`,
            label: i18n.t(
              'procurement.purchase_requests.settings.types.detail.tabs.workflows.title'
            ),
          },
        ].filter(Boolean)}
      />

      {Content}
    </>
  )
}

export default React.memo(TabContent)
